import { forwardRef } from 'react';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { disabledFade, focusRing } from '../lib/focus.js';

/* ===========================================================================
 * THE FILTER TRIGGER — the button, and its two shapes.
 *
 * It moved out of `FiltersPopover` when it grew the second shape. The popover
 * file is about a portalled dialog, its focus trap and its placement; the
 * trigger is about a toolbar control, its state layer and what it says when it
 * has no room for a word. Two subjects, and the second one is the one that now
 * has an opinion to state:
 *
 *   FULL      a glyph, the word "Filters", the count badge, a chevron.
 *   COMPACT   the glyph and the badge, square at the control height.
 *
 * COMPACT KEEPS BOTH THINGS THAT MAKE THE CONTROL HONEST. The badge stays,
 * because an icon that hid how many filters were on would leave a narrowed list
 * looking unnarrowed — §3, not cosmetics — and the name stays, as `aria-label`
 * and `title`, because a bare glyph is a question for a sighted reader as much
 * as for a screen-reader one. What compact drops is the word and the chevron:
 * the disclosure is already announced by `aria-expanded`, and two glyphs in a
 * 32px square is texture.
 * ======================================================================== */

export interface FiltersTriggerProps {
  /** The trigger's word — and, in compact, its accessible name. */
  label: string;
  /** How many filters are applied. `0` or `undefined` draws no badge. */
  count?: number;
  /** What the count means for a reader who never sees the badge. */
  countLabel?: string;
  open: boolean;
  onToggle: () => void;
  /** `id` of the panel, while it is open. */
  panelId?: string;
  /** Icon-only. See the note above — opt-in, never the default. */
  compact?: boolean;
}

export const FiltersTrigger = forwardRef<HTMLButtonElement, FiltersTriggerProps>(
  function FiltersTrigger({ label, count, countLabel, open, onToggle, panelId, compact }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={panelId}
        // The word is gone in compact, so the name arrives another way — `title`
        // as well as `aria-label`, because a sighted reader meeting a bare glyph
        // has the same question a screen-reader user does.
        aria-label={compact ? label : undefined}
        title={compact ? label : undefined}
        onClick={onToggle}
        className={cn(
          // `.state-layer` rather than a hand-written `hover:bg-*`: it is the
          // same currentColor hover/focus/press ramp `Button` and `IconButton`
          // use, so this control darkens and presses like the rest of the family
          // in either theme and for any tenant hue.
          'state-layer inline-flex items-center gap-element whitespace-nowrap',
          'min-h-control rounded-control border border-border bg-surface',
          // Square at the control height when there is no word to make it wide,
          // so it matches the icon tools it sits beside; the coarse floor grows
          // on BOTH axes, as an icon-only target must.
          compact
            ? 'min-w-control justify-center px-1 [@media(pointer:coarse)]:min-w-touch'
            : 'px-3',
          // 13.5px at 600 — the trigger is a toolbar BUTTON and takes the body
          // step the buttons either side of it take (`font-size: 13.5px;
          // font-weight: 600`, Hadir Operations.dc.html:237). `text-label` put
          // it at 12.5px, the one control in the bar reading a step small. The
          // weight is separate because `text-copy` is the 400 body step.
          'text-copy font-semibold text-foreground',
          '[@media(pointer:coarse)]:min-h-touch',
          'transition-[background-color,border-color] duration-fast ease-standard',
          // The handoff's hover sharpens the EDGE rather than shouting with the
          // fill, which is what keeps a toolbar of five controls quiet.
          'hover:border-border-strong',
          focusRing,
          cn('disabled:pointer-events-none disabled:transition-none', disabledFade)
        )}
      >
        <SlidersHorizontal size={15} aria-hidden="true" className="flex-none" />
        {!compact && label}
        {count != null && count > 0 && (
          <span
            className={cn(
              'inline-grid flex-none place-items-center rounded-xs px-1',
              // Minimum, not fixed — a three-digit count grows the pill instead
              // of overflowing it.
              'min-h-[1.125rem] min-w-[1.125rem]',
              // The brand `tint`/`deep` pairing: a pale wash with brand ink on
              // it. Never the solid `--primary` here — this is a readout, and
              // brand is for the active nav marker, the primary action and the
              // one series meaning "on site" (rules §1).
              'bg-primary-tint text-primary-deep',
              // `text-label` (12.5px), not the mock's 11px. Same reasoning that
              // moved `Badge` off `text-badge`: this is a figure a reader reads,
              // and it should match the badges elsewhere on the screen rather
              // than being the one 11px thing in the bar.
              'text-label'
            )}
          >
            {/* The digit is for the eye and the sentence is for the ear. A bare
                "2" announced after "Filters" tells a screen-reader user nothing
                about what the 2 counts. */}
            <span aria-hidden="true">{count}</span>
            <span className="sr-only">{countLabel ?? String(count)}</span>
          </span>
        )}
        {!compact && (
          <ChevronDown size={13} aria-hidden="true" className="flex-none text-foreground-tertiary" />
        )}
      </button>
    );
  }
);
